import React, { useState, useRef, useEffect } from 'react'
import { styles } from './index.sass'
import { Button, message, Card, Input, Spin as AntdSpin, Modal } from 'antd'
import axios from '../../api/axios_ml'
import { cleanHtmlContent } from '../../utils/htmlCleaner'
import { sendEmail, sendTestEmail } from '../../api/mailchimp'
import RichTextEditor from '../../components/richTextEditor'

export const Rundown = () => {
  const [topic, setTopic] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [progress, setProgress] = useState([])
  const editorContentRef = useRef('')
  const eventSourceRef = useRef(null)

  const isValidUrl = (string) => {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  const handleGenerate = async (regenerate = false) => {
    if (!topic.trim()) {
      message.error('Please enter an Apeiron Blog URL')
      return
    }

    if (!isValidUrl(topic.trim())) {
      message.error('Please enter a valid URL (e.g., https://www.blog.apeiron.life/post/the-link-between-parkinson-s-disease-and-golf)')
      return
    }

    setLoading(true)
    setProgress([])
    
    try {
      // Connect to SSE endpoint
      const url = `${process.env.REACT_APP_ML_API_ENDPOINT}/api/ml/generate-rundown-stream`
      const eventSource = new EventSource(`${url}?url=${encodeURIComponent(topic)}&regenerate=${true}`)
      eventSourceRef.current = eventSource

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data)
        
        if (data.step !== undefined) {
          // Update progress
          setProgress(prev => [...prev, data])
        } else if (data.status === 'success') {
          // Rundown generation completed
          const cleanedHtml = cleanHtmlContent(data.content_html)
          setContentHtml(cleanedHtml)
          message.success('Rundown email generated successfully!')
          eventSource.close()
          setLoading(false)
        } else if (data.status === 'error') {
          // Error occurred
          console.error('Error generating rundown email:', data.message)
          message.error(data.message || 'Failed to generate rundown email. Please try again.')
          eventSource.close()
          setLoading(false)
        }
      }

      eventSource.onerror = () => {
        console.error('Stream connection error')
        eventSource.close()
        setLoading(false)
      }

    } catch (err) {
      console.error('Error generating rundown email:', err)
      message.error(
        err.message || 'Failed to generate rundown email. Please try again.',
      )
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: 'Regenerate Rundown Email',
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  const handleSendEmail = () => {
    Modal.confirm({
      title: 'Send Email',
      content: 'Are you sure you want to send this email?',
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const subject = 'Rundown Email'
          const response = await sendEmail({ content: contentHtml, subject: subject })
          if (response.success === true) {
            message.success('Email sent successfully!')
          } else {
            message.error('Failed to send email. Please try again.')
          }
        } catch (err) {
          console.error('Error sending email:', err)
          message.error('Failed to send email. Please try again.')
        }
      }
    })
  }

  const handleSendTestEmail = async () => {
    const subject = 'Test Rundown Email'
    const response = await sendTestEmail({ html: contentHtml, subject: subject })
    if (response.success === true) {
      message.success('Test email sent successfully!')
    } else {
      message.error('Failed to send test email. Please try again.')
    }
  }

  return (
    <div className={styles}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="progress-steps">
              {progress.map((step, index) => (
                <div key={index} className={`progress-step step-${step.step} ${step.status}`}>
                  {step.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>Rundown Email Generator</h2>
        <div className="input-section">
          <Input
            placeholder="Enter Apeiron Blog URL (e.g., https://www.blog.apeiron.life/post/the-link-between-parkinson-s-disease-and-golf)"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <div className="actions">
            <Button type="primary" onClick={handleGenerate} loading={loading}>
              Generate Rundown
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={!contentHtml || loading}
            >
              Regenerate
            </Button>
            <Button onClick={handleCopy} disabled={!contentHtml}>
              Copy Content
            </Button>
            <Button onClick={handleEdit} disabled={!contentHtml}>
              Edit Content
            </Button>
            <Button onClick={handleSendTestEmail} disabled={!contentHtml}>
              Send Test Email
            </Button>
            <Button onClick={handleSendEmail} disabled={!contentHtml}>
              Send Email
            </Button>
          </div>
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                  <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </div>
                <RichTextEditor
                  value={editedContent}
                  onChange={handleEditorChange}
                  placeholder="Edit your Rundown content here..."
                />
              </div>
            ) : (
              <div
                className="content-preview"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
